import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { industries } from '../../data/industries';
import { services } from '../../data/services';
import { useInquiryForm } from '../../hooks/useInquiryForm';
import { buttonClasses } from '../../utils/buttonStyles';
import { compactErrors, emailError, requiredChoice, requiredText } from '../../utils/validation';
import { ConsentField } from './ConsentField';
import { FormField } from './FormField';
import { FormStatusNotice } from './FormStatusNotice';
import { FormSuccess } from './FormSuccess';
import { HoneypotField } from './HoneypotField';
type EmployerValues = {
  fullName: string;
  businessEmail: string;
  organization: string;
  jobTitle: string;
  industry: string;
  position: string;
  timeline: string;
  requirements: string;
  contactMethod: string;
  privacy: boolean;
};
const FORM_ID = 'employer';
const industryOptions = [...industries.map(i => ({
  value: i.name,
  label: i.name
})), {
  value: 'Other',
  label: 'Other'
}];
const timelineOptions = ['Within 1 month', '1–3 months', '3–6 months', 'More than 6 months', 'Exploring options'].map(v => ({
  value: v,
  label: v
}));
const contactOptions = ['Email', 'Video call', 'Phone call (we will arrange by email)'].map(v => ({
  value: v,
  label: v
}));
export function EmployerInquiryForm() {
  const [params] = useSearchParams();
  const initialValues = useMemo<EmployerValues>(() => {
    const service = services.find(s => s.slug === params.get('service'));
    const industry = industries.find(i => i.slug === params.get('industry'));
    return {
      fullName: '',
      businessEmail: '',
      organization: '',
      jobTitle: '',
      industry: industry?.name ?? '',
      position: '',
      timeline: '',
      requirements: service ? `I would like to discuss ${service.title}.\n\n` : '',
      contactMethod: '',
      privacy: false
    };
  }, [params]);
  const form = useInquiryForm<EmployerValues>({
    formId: FORM_ID,
    type: 'employer',
    initialValues,
    mailSubject: 'Hiring inquiry — Aurevane Search Partners',
    validate: v => compactErrors({
      fullName: requiredText(v.fullName, 'your full name', 2),
      businessEmail: emailError(v.businessEmail),
      organization: requiredText(v.organization, 'your organization'),
      jobTitle: requiredText(v.jobTitle, 'your job title'),
      industry: requiredChoice(v.industry, 'an industry'),
      position: requiredText(v.position, 'the position to be filled'),
      timeline: requiredChoice(v.timeline, 'an expected hiring timeline'),
      requirements: requiredText(v.requirements, 'your search requirements', 20),
      contactMethod: requiredChoice(v.contactMethod, 'a preferred contact method'),
      privacy: v.privacy ? undefined : 'Please confirm you have read the Privacy Policy.'
    }),
    summarize: v => [`Name: ${v.fullName}`, `Business email: ${v.businessEmail}`, `Organization: ${v.organization}`, `Job title: ${v.jobTitle}`, `Industry: ${v.industry}`, `Position to be filled: ${v.position}`, `Expected timeline: ${v.timeline}`, `Preferred contact: ${v.contactMethod}`, '', 'Search requirements:', v.requirements]
  });
  const {
    values,
    errors,
    status,
    setField
  } = form;
  const id = (key: keyof EmployerValues) => `${FORM_ID}-${key}`;
  if (status === 'success') {
    return <FormSuccess title="Thank you — your hiring inquiry has been received." text="A member of our search team will be in touch using your preferred contact method to arrange an initial conversation." resetLabel="Submit another inquiry" onReset={form.reset} data-id="element-106" data-mp="ps2doqk7lcim" />;
  }
  return <form noValidate onSubmit={form.handleSubmit} className="relative space-y-6" data-id="element-107" data-mp="266285m0ac9yh">
      <p className="text-sm text-plum/60" data-id="element-108" data-mp="2a7nr9p3exkh4">
        Fields marked <span className="text-copper-deep" data-id="element-109" data-mp="2aa9z7pu7u1zv">*</span> are required.
      </p>
      <HoneypotField id={`${FORM_ID}-website`} value={form.honeypot} onChange={form.setHoneypot} data-id="element-110" data-mp="28rs1qawg3nth" />
      <div className="grid gap-6 md:grid-cols-2" data-id="element-111" data-mp="23tqd7ag92wam">
        <FormField id={id('fullName')} label="Full Name" required autoComplete="name" value={values.fullName} onChange={v => setField('fullName', v)} error={errors.fullName} data-id="element-112" data-mp="2ipz6zwhdghfn" />
        <FormField id={id('businessEmail')} label="Business Email" type="email" required autoComplete="email" value={values.businessEmail} onChange={v => setField('businessEmail', v)} error={errors.businessEmail} data-id="element-113" data-mp="2dsphyhxwa7sc" />
        <FormField id={id('organization')} label="Organization" required autoComplete="organization" value={values.organization} onChange={v => setField('organization', v)} error={errors.organization} data-id="element-114" data-mp="31fnyffm0lfq9" />
        <FormField id={id('jobTitle')} label="Job Title" required autoComplete="organization-title" value={values.jobTitle} onChange={v => setField('jobTitle', v)} error={errors.jobTitle} data-id="element-115" data-mp="3ag3ysg7go6qw" />
        <FormField id={id('industry')} label="Industry" as="select" required options={industryOptions} value={values.industry} onChange={v => setField('industry', v)} error={errors.industry} data-id="element-116" data-mp="3p3kk515id3ad" />
        <FormField id={id('position')} label="Position to Be Filled" required placeholder="e.g. Chief Financial Officer" value={values.position} onChange={v => setField('position', v)} error={errors.position} data-id="element-117" data-mp="3g2weouu6kb93" />
        <FormField id={id('timeline')} label="Expected Hiring Timeline" as="select" required options={timelineOptions} value={values.timeline} onChange={v => setField('timeline', v)} error={errors.timeline} data-id="element-118" data-mp="36cb6qgsm9udi" />
        <FormField id={id('contactMethod')} label="Preferred Contact Method" as="select" required options={contactOptions} value={values.contactMethod} onChange={v => setField('contactMethod', v)} error={errors.contactMethod} data-id="element-119" data-mp="2xcxz7to1zfvf" />
        <FormField id={id('requirements')} className="md:col-span-2" label="Search Requirements" as="textarea" required rows={6} maxLength={3000} hint="Share the role context, essential experience, and any confidentiality considerations. Avoid sharing highly sensitive details at this stage." value={values.requirements} onChange={v => setField('requirements', v)} error={errors.requirements} data-id="element-120" data-mp="2negzo8b1tsxr" />
      </div>
      <ConsentField id={id('privacy')} checked={values.privacy} onChange={v => setField('privacy', v)} error={errors.privacy} purpose="to respond to this hiring inquiry" data-id="element-121" data-mp="k3luni89uy1r" />
      <FormStatusNotice status={status} message={form.message} mailtoHref={form.mailtoHref} data-id="element-122" data-mp="1cuvqznf4el2a" />
      <button type="submit" disabled={status === 'submitting'} className={buttonClasses('primary', 'lg')} data-id="element-123" data-mp="17tg6bwguugkf">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-124" data-mp="3slrsq94g8hvc" />
            Submitting…
          </> : 'Submit Hiring Inquiry'}
      </button>
    </form>;
}